import * as mutationTypes from '@/store/mutation-types'
import * as AnalyticsAPI from '@/api/analytics'

export const CACHE_PREFIX = 'lga_analytics_'
export const CACHE_DURATION_MINUTES = 10

const state = {
  variantAnalytics: null,
}

const getters = {
  getVariantAnalyticsData: (state) => state.variantAnalytics,
}

const actions = {
  async fetchVariantAnalytics({ commit }, { formId, variantId, timePeriod, forceRefresh = false }) {
    const startTime = Date.now()

    // Build cache key - now only uses formId
    const cacheKey = `${CACHE_PREFIX}${formId}`

    // Check localStorage for this form
    const cacheData = localStorage.getItem(cacheKey)

    const formCache = JSON.parse(cacheData || '{}')
    const now = Date.now()

    // Navigate to specific variant and time period
    const variantCache = formCache[variantId] || {}
    const cachedEntry = variantCache[timePeriod]

    let dataToReturn = null

    // Check if we have valid cached data for this specific variant and time period
    if (cachedEntry && !forceRefresh) {
      const cacheAge = now - cachedEntry.timestamp
      const isExpired = cacheAge >= (CACHE_DURATION_MINUTES * 60 * 1000)

      if (!isExpired) {
        dataToReturn = cachedEntry.data
      } else {
        delete variantCache[timePeriod]

        // Clean up empty variant cache
        if (Object.keys(variantCache).length === 0) {
          delete formCache[variantId]
        } else {
          formCache[variantId] = variantCache
        }

        localStorage.setItem(cacheKey, JSON.stringify(formCache))
      }
    }

    // If no valid cached data, fetch from API
    if (!dataToReturn) {
      try {
        const response = await AnalyticsAPI.getVariantAnalytics(formId, variantId, timePeriod)

        if (response?.data) {
          dataToReturn = response.data

          // Update cache with fresh data - nested structure
          if (!formCache[variantId]) {
            formCache[variantId] = {}
          }

          formCache[variantId][timePeriod] = {
            data: response.data,
            timestamp: now,
          }

          localStorage.setItem(cacheKey, JSON.stringify(formCache))
        } else {
          throw new Error('No data returned from API.')
        }
      } catch (error) {
        commit(mutationTypes.FETCH_FORM_VARIANT_ANALYTICS_ERROR, error.message || 'Unknown error')
        throw error
      }
    }

    // Update Vuex state
    if (dataToReturn) {
      commit(mutationTypes.FETCH_FORM_VARIANT_ANALYTICS_SUCCESS, dataToReturn)
    }

    return dataToReturn
  },

  // Helper action to clear cache manually
  clearVariantAnalyticsCache({ commit }, { formId, variantId = null, timePeriod = null }) {
    const cacheKey = `${CACHE_PREFIX}${formId}`

    const formCache = JSON.parse(localStorage.getItem(cacheKey) || '{}')

    if (variantId && timePeriod) {
      // Clear specific variant + time period only
      if (formCache[variantId] && formCache[variantId][timePeriod]) {
        delete formCache[variantId][timePeriod]

        // Clean up empty variant cache
        if (Object.keys(formCache[variantId]).length === 0) {
          delete formCache[variantId]
        }

        if (Object.keys(formCache).length === 0) {
          localStorage.removeItem(cacheKey)
        } else {
          localStorage.setItem(cacheKey, JSON.stringify(formCache))
        }
      }
    } else if (variantId) {
      // Clear entire variant (all time periods)
      if (formCache[variantId]) {
        delete formCache[variantId]

        if (Object.keys(formCache).length === 0) {
          localStorage.removeItem(cacheKey)
        } else {
          localStorage.setItem(cacheKey, JSON.stringify(formCache))
        }
      }
    } else {
      // Clear entire form cache (all variants and time periods)
      localStorage.removeItem(cacheKey)
    }
  },

  // Helper action to get cache info for debugging
  getCacheInfo({ commit }, { formId, variantId = null }) {
    const cacheKey = `${CACHE_PREFIX}${formId}`
    const cacheData = localStorage.getItem(cacheKey)

    if (cacheData) {
      const formCache = JSON.parse(cacheData)

      // If specific variant requested, return just that variant's cache
      if (variantId && formCache[variantId]) {
        return { [variantId]: formCache[variantId] }
      }

      return formCache
    }

    return null
  },

  // Helper to check what's in localStorage for all analytics
  debugAllAnalyticsCache({ commit }) {
    const allKeys = Object.keys(localStorage).filter(key => key.startsWith(CACHE_PREFIX))

    if (allKeys.length === 0) {
      return
    }

    allKeys.forEach(key => {
      const formId = key.replace(CACHE_PREFIX, '')
      const data = JSON.parse(localStorage.getItem(key) || '{}')
      const variants = Object.keys(data)
    })
  },

  // New helper to get all cached data for a specific form (useful for debugging)
  getFormCache({ commit }, { formId }) {
    const cacheKey = `${CACHE_PREFIX}${formId}`
    const cacheData = localStorage.getItem(cacheKey)

    if (cacheData) {
      const formCache = JSON.parse(cacheData)
      return formCache
    }

    return null
  }
}

const mutations = {
  [mutationTypes.FETCH_FORM_VARIANT_ANALYTICS_SUCCESS](state, data) {
    state.variantAnalytics = data || null
  },

  [mutationTypes.FETCH_FORM_VARIANT_ANALYTICS_ERROR](state, errorMessage) {
    // Reset analytics data on error
    state.variantAnalytics = null
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
